import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { productService, carBrandService, carModelService, categoryService } from '../services/api';
import ProductCard from '../components/ProductCard';
import Loading from '../components/Loading';

const ProductsPage = () => {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  
  // Autocomplete states
  const [categoryInput, setCategoryInput] = useState('');
  const [brandInput, setBrandInput] = useState('');
  const [modelInput, setModelInput] = useState('');
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);
  const [showBrandSuggestions, setShowBrandSuggestions] = useState(false);
  const [showModelSuggestions, setShowModelSuggestions] = useState(false);
  
  const ITEMS_PER_PAGE = 20;

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand, selectedModel, selectedCategory, allProducts]);

  useEffect(() => {
    document.title = 'المتجر - متجر قطع غيار السيارات';
    
    // Close suggestions when clicking outside
    const handleClickOutside = (e) => {
      if (!e.target.closest('.autocomplete-container')) {
        setShowCategorySuggestions(false);
        setShowBrandSuggestions(false);
        setShowModelSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.title = 'متجر قطع غيار السيارات';
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, brandsRes, modelsRes, categoriesRes] = await Promise.all([
        productService.getAll(),
        carBrandService.getAll(),
        carModelService.getAll(),
        categoryService.getAll()
      ]);
      
      const productsData = productsRes?.data?.data || [];
      const brandsData = brandsRes?.data?.data || [];
      const modelsData = modelsRes?.data?.data || [];
      const categoriesData = categoriesRes?.data?.data || [];
      
      setAllProducts(productsData);
      setBrands(brandsData);
      setModels(modelsData);
      setCategories(categoriesData);
      
      // Check if there's a model filter in URL
      const modelId = searchParams.get('model');
      if (modelId) {
        setSelectedModel(modelId);
      }
      
      // Check if there's a category filter in URL
      const categoryId = searchParams.get('category');
      if (categoryId) {
        setSelectedCategory(categoryId);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setAllProducts([]);
      setBrands([]);
      setModels([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allProducts];

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter(product => 
        product.category_id === parseInt(selectedCategory)
      );
    }

    // Filter by brand
    if (selectedBrand) {
      filtered = filtered.filter(product => {
        if (product.car_brands) {
          const brands = typeof product.car_brands === 'string' 
            ? JSON.parse(product.car_brands) 
            : product.car_brands;
          return brands.includes(parseInt(selectedBrand));
        }
        return false;
      });
    }

    // Filter by model
    if (selectedModel) {
      filtered = filtered.filter(product => {
        if (product.car_models) {
          const models = typeof product.car_models === 'string' 
            ? JSON.parse(product.car_models) 
            : product.car_models;
          return models.includes(parseInt(selectedModel));
        }
        return false;
      });
    }

    setProducts(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleResetFilters = () => {
    setSelectedBrand('');
    setSelectedModel('');
    setSelectedCategory('');
    setCategoryInput('');
    setBrandInput('');
    setModelInput('');
    setCurrentPage(1);
  };

  // Filter suggestions based on input
  const getFilteredCategories = () => {
    if (!categoryInput) return categories;
    return categories.filter(cat => 
      cat.name.toLowerCase().includes(categoryInput.toLowerCase())
    );
  };

  const getFilteredBrands = () => {
    if (!brandInput) return brands;
    return brands.filter(brand => 
      brand.name.toLowerCase().includes(brandInput.toLowerCase())
    );
  };

  const getFilteredModels = () => {
    if (!modelInput) return models;
    return models.filter(model => 
      model.name.toLowerCase().includes(modelInput.toLowerCase())
    );
  };

  // Handle selection from suggestions
  const handleCategorySelect = (category) => {
    setSelectedCategory(category.id.toString());
    setCategoryInput(category.name);
    setShowCategorySuggestions(false);
  };

  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand.id.toString());
    setBrandInput(brand.name);
    setShowBrandSuggestions(false);
  };

  const handleModelSelect = (model) => {
    setSelectedModel(model.id.toString());
    setModelInput(model.name);
    setShowModelSuggestions(false);
  };

  // Handle input changes
  const handleCategoryInputChange = (e) => {
    const value = e.target.value;
    setCategoryInput(value);
    setShowCategorySuggestions(true);
    if (!value) {
      setSelectedCategory('');
    }
  };

  const handleBrandInputChange = (e) => {
    const value = e.target.value;
    setBrandInput(value);
    setShowBrandSuggestions(true);
    if (!value) {
      setSelectedBrand('');
    }
  };

  const handleModelInputChange = (e) => {
    const value = e.target.value;
    setModelInput(value);
    setShowModelSuggestions(true);
    if (!value) {
      setSelectedModel('');
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(products.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentProducts = products.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxButtons = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);
    
    if (endPage - startPage < maxButtons - 1) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-4 py-2 mx-1 rounded-lg ${
            currentPage === i
              ? 'bg-primary text-white'
              : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          {i}
        </button>
      );
    }
    
    return buttons;
  };

  if (loading) return <Loading />;

  return (
    <div className="container mx-auto px-4 py-4 sm:py-8" dir="rtl">
      {/* Mobile Filter Toggle Button */}
      <button
        onClick={() => setShowFilters(!showFilters)}
        className="md:hidden w-full bg-primary text-white px-4 py-3 rounded-lg mb-4 flex items-center justify-between font-semibold"
      >
        <span>تصفية المنتجات</span>
        <svg
          className={`w-5 h-5 transition-transform ${showFilters ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Filters */}
      <div className={`bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-8 ${showFilters ? 'block' : 'hidden md:block'}`}>
        <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-right hidden md:block">تصفية المنتجات</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-3 sm:mb-4">
          {/* Category Filter */}
          <div className="relative autocomplete-container">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2 text-right">
              القسم
            </label>
            <input
              type="text"
              value={categoryInput}
              onChange={handleCategoryInputChange}
              onFocus={() => setShowCategorySuggestions(true)}
              placeholder="ابحث عن القسم..."
              className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {showCategorySuggestions && getFilteredCategories().length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {getFilteredCategories().map(category => (
                  <div
                    key={category.id}
                    onClick={() => handleCategorySelect(category)}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-right text-sm sm:text-base"
                  >
                    {category.name}
                  </div>
                ))}
              </div>
            )}
            {selectedCategory && (
              <button
                onClick={() => {
                  setSelectedCategory('');
                  setCategoryInput('');
                }}
                className="absolute left-3 top-9 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>

          {/* Brand Filter */}
          <div className="relative autocomplete-container">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2 text-right">
              الماركة
            </label>
            <input
              type="text"
              value={brandInput}
              onChange={handleBrandInputChange}
              onFocus={() => setShowBrandSuggestions(true)}
              placeholder="ابحث عن الماركة..."
              className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {showBrandSuggestions && getFilteredBrands().length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {getFilteredBrands().map(brand => (
                  <div
                    key={brand.id}
                    onClick={() => handleBrandSelect(brand)}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-right text-sm sm:text-base"
                  >
                    {brand.name}
                  </div>
                ))}
              </div>
            )}
            {selectedBrand && (
              <button
                onClick={() => {
                  setSelectedBrand('');
                  setBrandInput('');
                }}
                className="absolute left-3 top-9 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>

          {/* Model Filter */}
          <div className="relative autocomplete-container">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2 text-right">
              الموديل
            </label>
            <input
              type="text"
              value={modelInput}
              onChange={handleModelInputChange}
              onFocus={() => setShowModelSuggestions(true)}
              placeholder="ابحث عن الموديل..."
              className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {showModelSuggestions && getFilteredModels().length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {getFilteredModels().map(model => (
                  <div
                    key={model.id}
                    onClick={() => handleModelSelect(model)}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-right text-sm sm:text-base"
                  >
                    {model.name}
                  </div>
                ))}
              </div>
            )}
            {selectedModel && (
              <button
                onClick={() => {
                  setSelectedModel('');
                  setModelInput('');
                }}
                className="absolute left-3 top-9 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Reset Filters Button */}
        {(selectedBrand || selectedModel || selectedCategory) && (
          <button
            onClick={handleResetFilters}
            className="bg-gray-500 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-gray-600 text-sm sm:text-base w-full sm:w-auto"
          >
            إعادة تعيين التصفية
          </button>
        )}
      </div>

      {/* Products Count */}
      <div className="mb-3 sm:mb-4 text-right">
        <p className="text-sm sm:text-base text-gray-600">
          عرض {startIndex + 1}-{Math.min(endIndex, products.length)} من {products.length} منتج
        </p>
      </div>

      {/* Products Grid */}
      {currentProducts.length > 0 ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {currentProducts.map(product => (
              <ProductCard key={product.id} product={product} allModels={models} />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center mt-6 sm:mt-8 gap-1 sm:gap-2 flex-wrap">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base ${
                  currentPage === 1
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                ←
              </button>

              <div className="hidden sm:flex">
                {renderPaginationButtons()}
              </div>
              
              {/* Mobile: Show only current page */}
              <div className="sm:hidden flex items-center px-3 py-2 bg-white rounded-lg">
                <span className="text-sm text-gray-700">
                  {currentPage} / {totalPages}
                </span>
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base ${
                  currentPage === totalPages
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                →
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-8 sm:py-12">
          <p className="text-lg sm:text-xl text-gray-600">لا توجد منتجات</p>
        </div>
      )}
    </div>
  );
};

export default ProductsPage;
